import { signout } from "../NavBar/signout";

const keySessionTimoutStart = "sessionTimeoutStart";
const keySessionTimeoutMessage = "sessionTimeoutMessage";

/**
 * SessionTimeoutManager is a singleton class that manages the session timeout state for the application.
 *
 * Automatic session timeouts are an optional account-level feature that, when enabled, will automatically log a user
 * out after a period of inactivity. Inactivity is defined as a period of time when the user has not made any requests
 * to the backend via the `makeRequest` function. A session is considered inactive if the user has not made any requests
 * since the beginning of the current session timeout. The timeout clock restarts to zero on each request made to the
 * backend via `makeRequest`. We're using the concept of calling `makeRequest` as a proxy for user activity, as this is
 * far simpler to instrument than tracking every single user interaction in the application via JavaScript event
 * handling. While any measure of user "activity" might be imperfect, our application happens to be quite noisy in terms
 * of sending HTTP requests to our backend. This imperfect measure is a reasonable approximation of user activity that
 * greatly simplifies how we can keep track of active session state.
 *
 * The session timeout manager is designed to work in conjunction with our `makeRequest` function, which will
 * automatically terminate an authentication session if the session timeout has expired.
 *
 * Session timeouts are configured at the account level, under the `AccountMetadata.config.sessionTimeoutMS` property.
 *
 * If unset, the session timeout manager will not be created, and the application will not have a session timeout.
 *
 * If set, the session timeout manager will be created, and the application will have a session timeout lasting the
 * number of milliseconds specified in the account's configuration settings.
 *
 * Session timeouts are evaluated on each request made to the backend. If the session timeout has expired, the user will
 * be logged out immediately. If the session timeout has not expired, the session timeout will be started again using
 * the account's configured maximum session timeout duration.
 *
 * @see https://www.notion.so/freshpaintio/RFC-Session-Inactivity-Timeouts-1c81ea732c1e8025b4dec091c32652d2
 */
export default class SessionTimeoutManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private readonly sessionTimeoutMS: number;
  private warningTimeoutId: NodeJS.Timeout | null = null;

  constructor(sessionTimeoutMS: number) {
    // Make sure the session timeout manager is a singleton. We don't want to have multiple session timeout managers
    // created at the same time because they might conflict, or track different timeouts.
    if (window.sessionTimeoutManager !== undefined) {
      throw new Error("SessionTimeoutManager already exists");
    }

    this.sessionTimeoutMS = sessionTimeoutMS;

    window.sessionTimeoutManager = this;
  }

  /**
   * Begin a session timeout. This will start a timer that will call the provided function when the timeout expires.
   */
  beginTimeout(onTimeout: () => void): void {
    this.clearTimeout();

    this.saveSessionTimeoutStart(new Date());
    this.timeoutId = setTimeout(onTimeout, this.sessionTimeoutMS);

    // Set warning timer for 1 minute before timeout
    if (this.sessionTimeoutMS > 30000) {
      this.warningTimeoutId = setTimeout(() => {
        if (
          window.confirm(
            "You have 30 seconds left before logout due to inactivity. Click OK to stay logged in.",
          )
        ) {
          this.beginTimeout(onTimeout); // Reset the timeout
        }
      }, this.sessionTimeoutMS - 30000);
    }
  }

  /**
   * Whether the configured session timeout has expired.
   */
  isTimeoutExpired(): boolean {
    const start = window.localStorage.getItem(keySessionTimoutStart);
    if (!start) {
      return false;
    }

    const startDate = new Date(start);
    const now = new Date();

    return now.getTime() - startDate.getTime() > this.sessionTimeoutMS;
  }

  /**
   * Clear the session timeout. This will stop the timer and remove the session timeout state from local storage.
   */
  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }

    window.localStorage.removeItem(keySessionTimoutStart);
  }

  /**
   * For each request made to our backend, we should verify whether the session timeout has expired. If it has, we must
   * immediately log the user out.
   *
   * Masquerade sessions are not subject to session timeouts, so we don't need to check for them.
   *
   * Part of the logout process is to invalidate the session token, which will trigger a request to the backend. This is
   * a unique exception case that we must have in place or we'll create an infinite loop of logout requests to log out
   * a stale session.
   */
  verifySessionTimeoutOrLogOut(
    requestKind: string,
    isMasqueradeSession: boolean,
  ): void {
    if (requestKind === "invalidate_token") {
      return;
    }

    if (isMasqueradeSession) {
      return;
    }

    if (this.isTimeoutExpired()) {
      SessionTimeoutManager.onSessionTimeout();
      return;
    }

    this.beginTimeout(SessionTimeoutManager.onSessionTimeout);
  }

  /**
   * Initialize the session timeout manager for a new session with the account's configuration settings for session
   * timeouts.
   *
   * This will clean up any existing session timeout state, and if a session timeout is provided, will create a new
   * session timeout manager and start the timeout.
   */
  static initialize(sessionTimeoutMS?: number): void {
    // Clear the existing session timeout manager to ensure that we don't have a stale session timeout manager with a
    // outdated setting.
    SessionTimeoutManager.teardown();

    // If nothing is configured for this account, we don't need to do anything further.
    if (sessionTimeoutMS === undefined) {
      return;
    }

    window.sessionTimeoutManager = new SessionTimeoutManager(sessionTimeoutMS);

    window.sessionTimeoutManager.beginTimeout(
      SessionTimeoutManager.onSessionTimeout,
    );
  }

  /**
   * Callback to invoke whenever a user logs in or out of the application.
   *
   * Nominally this is assumed to be called by the Firebase handler `firebase.auth().onAuthStateChanged()`.
   */
  static onAuthStateChanged(isUserLoggedIn: boolean): void {
    // Clean up any existing timeouts whenever a user logs in or out of the app.
    window.sessionTimeoutManager?.clearTimeout();

    // When logging a user in, we want to clear the session timeout message if any exists.
    // This logout message is used to explain to users why they were logged out if the logout occurred automatically
    // because of a session timeout.
    if (isUserLoggedIn) {
      SessionTimeoutManager.clearLogoutMessage();
    } else {
      SessionTimeoutManager.teardown();
    }
  }

  /**
   * Explanatory message to display to the user when they are logged out due to inactivity. This message is stored in
   * local storage so that it can be displayed to the user after they are logged out.
   *
   * This message is helpful because it might surprise a user to open Freshpaint and see the login screen without other
   * explanation.
   */
  static logoutMessage(): string {
    return window.localStorage.getItem(keySessionTimeoutMessage) || "";
  }

  private static clearLogoutMessage(): void {
    window.localStorage.removeItem(keySessionTimeoutMessage);
  }

  /**
   * The default callback to invoke when the session timeout expires. This will clear the session timeout manager and
   * log the user out.
   */
  static onSessionTimeout(): void {
    SessionTimeoutManager.teardown();

    window.localStorage.setItem(
      keySessionTimeoutMessage,
      "You were automatically logged out due to inactivity.",
    );

    signout();
  }

  /**
   * Clean up any existing session timeout manager from global state.
   */
  static teardown(): void {
    window.sessionTimeoutManager?.clearTimeout();
    window.sessionTimeoutManager = undefined;
  }

  private saveSessionTimeoutStart(start: Date): void {
    window.localStorage.setItem(keySessionTimoutStart, start.toISOString());
  }
}
